import { Modal } from 'antd-mobile-v5'
import { useIntl } from 'react-intl'
import { usePagination, useRequest } from 'ahooks'

import { debug } from '../utils/env'
import { getCookie } from '../components/common/cookie'
import * as config from './config'
import { isCert } from '../utils/cert'
import { formatDate } from '../components/common/utils'

function getApiRoot() {
  const origin = document.location.origin
  if (origin === config.URL_prod || origin === config.URL_prod_tw) {
    return config.baseURL_prod
  } else {
    return config.baseURL_qa
  }
}

export function deepLoop(obj) {
  if (typeof obj !== 'object' || obj == null) {
    return obj
  }
  if (Object.prototype.toString.call(obj).slice(8, -1) === 'Date') {
    return formatDate(obj, 'YYYY-MM-DD')
  }
  let result
  if (obj instanceof Array) {
    result = []
  } else {
    result = {}
  }
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      result[key] = deepLoop(obj[key])
    }
  }
  return result
}

const API_ROOT = getApiRoot()
const ossHost = 'https://ecards-confirm-apps.oss-cn-beijing.aliyuncs.com'
const fetchWrapper = async (method, withToken, endpoint, request = {}, responseType = 'json', cache) => {
  const cookieLanguage = getCookie('language')
  const language = cookieLanguage === 'tc' ? 'zh_TW' : cookieLanguage === 'en' ? 'en' : 'zh_CN'
  const options = {
    method,
    headers: {
      'content-type': 'application/json; charset=utf-8',
    },
  }
  // cache 参数保留为通用开关；建单类 GET 的禁缓存已由后端统一处理
  // （/api/** 响应全局下发 Cache-Control: no-store，后端 9d1127d81），调用方不再传 'no-store'
  if (cache) options.cache = cache
  if (withToken && getCookie('padiWxId')) options.headers.Authorization = getCookie('padiWxId')

  let url = `${API_ROOT}${endpoint}`
  const newRequest = deepLoop(request)
  if (method === 'POST' || method === 'PUT') {
    options.body = JSON.stringify(newRequest)
    url += `?lang=${language}`
  } else if ((method === 'GET' || method === 'DELETE') && endpoint.indexOf('getWxJsSign') < 0) {
    newRequest.lang = language
    // URLSearchParams 会把 undefined/null 序列化成字符串 "undefined"/"null"（与 POST JSON.stringify
    // 自动丢弃不同），过滤无效值避免把垃圾参数发给后端（如 PC 场景缺省的 openid/payType）
    Object.keys(newRequest).forEach((key) => {
      if (newRequest[key] == null) delete newRequest[key]
    })
    const queryObj = new URLSearchParams(newRequest)
    url += `?${queryObj.toString()}`
  }
  // debug 打印前剥离敏感信息：openid 参数脱敏、Authorization 头不落 console（QA 环境日志暴露面）
  const debugUrl = url.replace(/([?&]openid=)[^&]*/g, '$1***')
  const debugOptions = { ...options, headers: { ...options.headers } }
  delete debugOptions.headers.Authorization
  debug('Fetch url: ', debugUrl)
  debug('Fetch options: ', debugOptions)
  const result = await fetch(url, options)
    .then((response) => {
      debug('Response type: ', response.type)
      debug('Response ', response)
      if (response.ok) {
        const contentType = response.headers.get('content-type')
        const contentLength = response.headers.get('content-length')
        if (
          responseType === 'json' &&
          (!contentType || contentType.indexOf('application/json') === -1 || contentLength === '0')
        ) {
          return {}
        }
        return response[responseType]()
      } else {
        debug('Fetch error: ', 'serve error')
        throw 'serve error'
      }
    })
    .catch((err) => {
      debug('Fetch error: ', err)
      throw err
      // return err
    })
  debug('Fetch Result: ', result)
  if (result.error) throw result.error
  return result
}

const fetchOssFile = async (endpoint, formData) => {
  const options = {
    method: 'POST',
    headers: {
      Authorization: formData.get('policy'),
    },
    body: formData,
  }
  const url = endpoint
  try {
    const response = await fetch(url, options)
    if (!response.ok) throw new Error(`HTTP code: ${response.status}, ${response.statusText}`)
    const data = { credentialId: parseInt(formData.get('key').split('/')[2]), url: formData.get('key') }
    return data
  } catch (error) {
    return { error }
  }
}

export const getService = (url, params, responseType = 'json') => fetchWrapper('GET', false, url, params, responseType)
export const postService = (url, params, responseType = 'json') =>
  fetchWrapper('POST', false, url, params, responseType)
export const authedGetService = (url, params, responseType = 'json', cache) =>
  fetchWrapper('GET', true, url, params, responseType, cache)
export const authedPostService = (url, params, responseType = 'json') =>
  fetchWrapper('POST', true, url, params, responseType)
export const authedPutService = (url, params, responseType = 'json') =>
  fetchWrapper('PUT', true, url, params, responseType)
export const authedDeleteService = (url, params, responseType = 'json') =>
  fetchWrapper('DELETE', true, url, params, responseType)
const authedPagedService =
  (url) =>
  async ({ current, pageSize }, params) => {
    const { totalElements, content } = await fetchWrapper('GET', true, url, {
      page: current || 1,
      size: pageSize || 20,
      ...params,
    })
    return { total: totalElements, list: content }
  }
export const useErrorModal = (modalOptions = {}) => {
  const { formatMessage } = useIntl()
  return (content, url, hideTitle) => {
    Modal.alert({
      title: hideTitle ? '' : formatMessage({ id: 'common-errorTitle' }),
      content: content || formatMessage({ id: 'common-errorStatus' }),
      confirmText: formatMessage({ id: 'common-know' }),
      onConfirm: () => {
        if (url) window.location.href = url
      },
      ...modalOptions,
    })
  }
}
export const useRequestCommon = (service, options = {}, noDialog) => {
  const showErrorModal = useErrorModal()
  const { formatMessage } = useIntl()
  return useRequest(service, {
    manual: true,
    // staleTime: 1000 * 60 * 30, // 30 mins
    ...options,
    onError: (error, params) => {
      const { code, clientMsg } = error
      if (noDialog) return
      if (code === 'auth.required' || code === 'expired.token') {
        showErrorModal(clientMsg, `/emailLogin?referer=${window.location.href}`)
      } else if (code === 'email.not.verified') {
        showErrorModal(clientMsg, '/register')
      } else if (options.onError) {
        const handled = options.onError(error, params)
        if (!handled) {
          if (code === 'mobile.not.verified') {
            showErrorModal(clientMsg, false, true)
            return
          }
          showErrorModal(clientMsg)
        }
      } else if (code === 'sys.error.0') {
        showErrorModal(clientMsg, '/myProfile')
      } else {
        console.error(error)
        showErrorModal(clientMsg)
      }
    },
  })
}

const usePaginationCommon = (pagedService, options = {}) => {
  const showErrorModal = useErrorModal()
  return usePagination(pagedService, {
    manual: false,
    // staleTime: 1000 * 60 * 30, // 30 mins
    ...options,
    onError: (error, params) => {
      const { code, clientMsg } = error
      if (code === 'auth.required') {
        showErrorModal(clientMsg, '/emailLogin')
      } else if (options.onError) {
        const handled = options.onError(error, params)
        if (!handled) {
          showErrorModal(clientMsg)
        }
      } else {
        showErrorModal(clientMsg)
      }
    },
  })
}

export const useStudentSearchByEmail = (options) =>
  useRequestCommon((email, type) => authedGetService(config.searchStudentByEmail, { email, type }), options)

export const useStoreSearch = (options) =>
  useRequestCommon((storeNumber) => authedGetService(config.storeInfo, { storeNumber }), options)

export const useInstructorSearch = (options) =>
  useRequestCommon(
    (instructorNumber, instructorType) =>
      authedGetService(config.instructorInfo, {
        instructorNumber,
        instructorType,
      }),
    options
  )

export const useIssueCertsCreate = (options) =>
  useRequestCommon((data) => authedPostService(config.efrpAppSubmit, data), options)

export const useIssueCertsList = (options) => usePaginationCommon(authedPagedService(config.efrpAppList), options)

export const useIssueCertsPay = (options) =>
  useRequestCommon((params) => {
    // 兼容旧调用方式: 只传 code (number/string)，也支持 Fuiou 的 { code, channel }
    const query = typeof params === 'object' && params !== null ? params : { code: params }
    return authedGetService(config.efrpAppPay, query)
  }, options)

export const useTextbookOrderAddress = (options) =>
  useRequestCommon(() => authedGetService(config.textBookSaleAddress), {
    manual: false,
    ...options,
  })

export const useTextbookOrder = (options) =>
  useRequestCommon((params) => authedPostService(config.textBookSaleOrder, params), options)

export const useTextbookOrderCreate = (options) =>
  useRequestCommon((params) => authedPostService(config.createTextBookSaleOrder, params), options)

export const useIssueCertsConfirm = (options) =>
  useRequestCommon((code) => authedPostService(config.efrpAppConfirm, { code }), options)

export const useEFRPublicAppSubmit = (options) =>
  useRequestCommon(() => authedPostService(config.efrPublicAppSubmit), options)

export const useEFRPublicAppDetail = (options) =>
  useRequestCommon(() => authedGetService(config.efrPublicAppDetail, {}), options)

export const useEcards = (options) =>
  useRequestCommon(() => authedGetService(config.ecards, {}), {
    manual: false,
    cacheKey: 'ecards',
    ...options,
  })

export const useAddToCart = (options) =>
  useRequestCommon((items) => authedPostService(config.addToCart, items), options)

export const useEcardOrderCreate = (options) =>
  useRequestCommon((params) => authedPostService(config.createEcardOrder, params), options)

// 我的可用优惠券（ecard_coupon UNUSED 且未过期）：结算弹窗展示并携带 couponId 下单。
// 后端 2026-08-22 起 /coupons 改为全状态分页（Page<EcardCouponDTO>）：传 status=UNUSED 保持
// 可用券语义（服务端对 UNUSED 仍过滤过期），响应取 content 数组。
// 不加 cacheKey——支付成功后页面 reload，下次进入自然刷新（券用掉后列表不过期）
export const useEcardCoupons = (options) =>
  useRequestCommon(
    () => authedGetService(config.ecardCoupons, { status: 'UNUSED' }).then((res) => (res && res.content) || []),
    {
      manual: false,
      ...options,
    }
  )

// 订单金额试算：计价与 createEcardOrder 完全同源（含优惠券预估折扣），只试算不落单。
// 响应金额字段为字符串：{ itemsPrice, expressPrice, couponPrice|null, totalPrice }
export const useEcardOrderPreview = (options) =>
  useRequestCommon((params) => authedPostService(config.ecardOrderPreview, params), {
    manual: true,
    ...options,
  })

// 商品目录（ecard_goods 全量 CNY 上架商品）：createEcardOrder 的 itemNo 唯一取值来源。
// cardOptions（/api/card/replacementOptions）是有赞缓存体系、不含 itemNo，不能作为 SKU 来源。
export const useEcardGoods = (options) =>
  useRequestCommon(() => authedGetService(config.ecardGoods, {}), {
    manual: false,
    cacheKey: 'ecardGoods',
    ...options,
  })

export const useEcardPaymentStatus = (options) =>
  useRequestCommon((id) => authedGetService(config.ecardPaymentStatus, { id }), {
    // 轮询场景：ahooks 3.7.11 无 throwOnError 选项（被静默忽略），runAsync 失败恒 reject；
    // 页面用 runAsync 消费 rejection，useFuiouPaymentPolling 的连续失败计数才能生效
    ...options,
  })

export const useEcardCloseOrder = (options) =>
  useRequestCommon((id) => authedPostService(config.ecardCloseOrder, { id }), {
    ...options,
  })

export const useAgentElearningPaymentStatus = (options) =>
  useRequestCommon((code) => authedGetService(config.agentElearningPaymentStatus, { code }), {
    // 轮询场景：ahooks 3.7.11 无 throwOnError 选项（被静默忽略），runAsync 失败恒 reject；
    // 页面用 runAsync 消费 rejection，useFuiouPaymentPolling 的连续失败计数才能生效
    ...options,
  })

export const useAgentElearningCloseOrder = (options) =>
  useRequestCommon((code) => authedPostService(config.agentElearningCloseOrder, { id: code }), {
    ...options,
  })

export const useShopCart = (options) =>
  useRequestCommon(() => authedGetService(config.getShoppingCart), {
    // manual: false,
    ...options,
  })

export const useCardOptions = ({ noDialog, ...options } = {}) =>
  useRequestCommon(
    () => authedGetService(config.cardOptions),
    {
      manual: false,
      ...options,
    },
    noDialog
  )

export const useGetFreeCred = (options) =>
  useRequestCommon((ecardId) => authedPostService(config.credentialRequest, { ecardId }), {
    ...options,
  })

export const useHasLogin = (options, page = 'list') =>
  useRequestCommon(() => authedGetService(config.hasLogin, { page }), {
    manual: false,
    ...options,
  })

export const usePwInfo = (options) =>
  useRequestCommon(() => authedGetService(config.pwInfo), {
    manual: false,
    ...options,
  })

export const useProfile = (options) =>
  useRequestCommon(() => authedGetService(config.profiles), {
    manual: false,
    ...options,
  })

export const useDownloadWallCert = (options) => {
  const showErrorModal = useErrorModal()

  return useRequestCommon((data) => authedGetService(config.downloadWallCert, data, 'blob'), {
    onSuccess: (blob) => {
      if (blob.type === 'application/json') {
        blob.text().then((text) => {
          const result = JSON.parse(text)
          showErrorModal(result?.error?.clientMsg)
        })
        return
      }
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // a.target = '_blank';
      a.download = 'download.jpg'
      document.body.appendChild(a)
      a.click()
      a.remove()
    },
    ...options,
  })
}

export const useQualification = (cert, options) =>
  useRequestCommon(
    isCert(cert)
      ? () =>
          authedGetService(
            config.certificationQualifications,
            {
              certificationId: cert.certificationNumber,
            },
            'text'
          )
      : () =>
          authedGetService(
            config.credentialQualifications,
            {
              affiliateCredentialId: cert.affiliateCredentialId,
            },
            'text'
          ),
    {
      manual: false,
      ...options,
    }
  )

export const useHasActivity = (activityCode, options) =>
  useRequestCommon(() => authedGetService(config.hasActivityCard, { activityCode }), {
    manual: false,
    ...options,
  })

export const useBindEcard = (options) =>
  useRequestCommon((data) => authedPostService(config.ecardImgBinding, data), {
    ...options,
  })

export const useNotification = (position, options) =>
  useRequestCommon(() => authedGetService(config.notification + position), {
    manual: false,
    ...options,
  })

export const useGetRegulatorCoupon = (options) =>
  useRequestCommon(() => authedGetService(config.getRegulatorCoupon), {
    manual: false,
    ...options,
  })

export const useCheckRegulatorCoupon = (options) =>
  useRequestCommon((data) => postService(config.checkRegulatorCoupon, data), {
    ...options,
  })

export const useGetStandardRegions = (options) =>
  useRequestCommon(() => authedGetService(config.getStandardRegions), {
    manual: false,
    ...options,
  })

export const useMergeCerts = (options) =>
  useRequestCommon(() => authedPostService(config.certsMerge, {}), {
    ...options,
  })

export const useAdminMergeCerts = (options) =>
  useRequestCommon((data) => authedPostService(config.mergeCerts, data), {
    ...options,
  })

export const useMergeRefind = (options) =>
  useRequestCommon((findId) => authedPutService(config.refindMergeCerts, { findId }), {
    ...options,
  })

export const useDeleteMergeCerts = (options) =>
  useRequestCommon((findId) => authedDeleteService(config.deleteMergeCerts, { findId }), {
    ...options,
  })

export const useAdminMergeComplete = (options) =>
  useRequestCommon((id) => authedPostService(config.mergeComplete, { id }), {
    ...options,
  })

export const useFindMeSearch = (options) =>
  useRequestCommon((data) => authedGetService(config.findMeSearch, data), {
    // manual: false,
    ...options,
  })

export const useDeleteMemberApplication = (options) =>
  useRequestCommon((data) => authedDeleteService(config.cancelApplication, data), {
    ...options,
  })

export const useFindAccountRefind = (options) =>
  useRequestCommon((findId) => authedPutService(config.findAccountRefind, { findId }), {
    ...options,
  })

export const useDeleteFindAccount = (options) =>
  useRequestCommon((findId) => authedDeleteService(config.findAccountDelete, { findId }), {
    ...options,
  })

export const useAdminMergeAccount = (options) =>
  useRequestCommon((data) => authedPostService(config.findAccuntMerge, data), {
    ...options,
  })

export const useAdminFindAccountComplete = (options) =>
  useRequestCommon((id) => authedPostService(config.findAccountMergeComplete, { id }), {
    ...options,
  })

export const useSubmitFindMeByRequest = (options) =>
  useRequestCommon((data) => authedPostService(config.submitFindMeRequest, data), {
    ...options,
  })

export const useUpdateUserInfo = (options) =>
  useRequestCommon((data) => authedPostService(config.updateUserInfo, data), {
    ...options,
  })

export const useUpdateUserInfoApproved = (options) =>
  useRequestCommon((data) => authedPutService(config.adminApproveUpdateUserInfo, data), {
    ...options,
  })
export const useAdminDeleteMemberApplication = (options) =>
  useRequestCommon((data) => authedDeleteService(config.adminCancelApplication, data), {
    ...options,
  })

export const useAdminPaymentMemberApplication = (options) =>
  useRequestCommon((data) => authedPutService(config.adminPaymentApplication, data), {
    ...options,
  })

export const useAdminUpdateComplete = (options) =>
  useRequestCommon((id) => authedPostService(config.adminCompleteUpdate, { id }), {
    ...options,
  })

export const useSubmitUserAgreement = (options) =>
  useRequestCommon((data) => authedPostService(config.UserAgreementSubmit, data), {
    ...options,
  })

export const useCheckAgreement = (options) =>
  useRequestCommon(() => authedGetService(config.checkAgreement), {
    manual: false,
    ...options,
  })

export const useCheckRegisterAgreement = (options) =>
  useRequestCommon(() => authedGetService(config.checkRegisterAgreement), {
    manual: false,
    ...options,
  })

export const useAdminFindmeRegister = (options) =>
  useRequestCommon((id) => authedPostService(config.adminFindmeRegister, { id }), {
    ...options,
  })

export const useAdminWenjuanApproved = (options) =>
  useRequestCommon((data) => authedPutService(config.adminWenjuanApproved, data), {
    ...options,
  })

export const useGetOssSignature = (type, options) =>
  useRequestCommon(() => authedGetService(config.ossSignature, { type }), {
    manual: false,
    ...options,
  })

export const useGetOssFullUrl = (key, options) =>
  useRequestCommon((key) => authedGetService(config.getOssFile, { key }, 'text'), {
    ...options,
  })

export const useGetOtherAppsTypeList = (manual, options) =>
  useRequestCommon(() => authedGetService(config.getOtherAppsTypeList, {}), {
    manual: !manual,
    ...options,
  })

export const useAdminOtherAppsApproved = (options) =>
  useRequestCommon((data) => authedPutService(config.adminOtherAppsApproved, data), {
    ...options,
  })

export const useGetOtherAppsList = (current, options) =>
  useRequestCommon(() => authedGetService(config.getOtherAppsList, { page: current, size: 15 }), {
    // manual: false,
    ...options,
  })

export const useUploadFileToOss = (options) =>
  useRequestCommon((ossHost, formData) => fetchOssFile(ossHost, formData), {
    ...options,
  })

export const useSubmitOtherApps = (options) =>
  useRequestCommon((data) => authedPostService(config.submitOtherApps, data), {
    ...options,
  })

export const usePayOtherApps = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.otherAppsPay, query)
    },
    {
      ...options,
    }
  )

export const useGetAdsList = (options) =>
  useRequestCommon(() => authedGetService(config.adsList, {}), {
    manual: false,
    ...options,
  })

export const useGetStoreAppsList = (current, options) =>
  useRequestCommon(() => authedGetService(config.getStoreAppsList, { page: current, size: 15 }), {
    // manual: false,
    ...options,
  })

export const useSubmitStoreApps = (options) =>
  useRequestCommon((data) => authedPostService(config.submitStoreApps, data), {
    ...options,
  })

export const useSubmitAds = (options) =>
  useRequestCommon((data) => authedPostService(config.submitAds, data), {
    ...options,
  })

export const useAdminStoreAppsApproved = (options) =>
  useRequestCommon((data) => authedPutService(config.adminStoreAppsApproved, data), {
    ...options,
  })

export const useDeletePocketApplication = (options) =>
  useRequestCommon((data) => authedDeleteService(config.deleteOtherApps, data), {
    ...options,
  })

export const useDeleteStoreApplication = (options) =>
  useRequestCommon((data) => authedDeleteService(config.deleteStoreApps, data), {
    ...options,
  })

export const usePayDm = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.dmPay, query)
    },
    {
      ...options,
    }
  )

export const useSaveInvoice = (options) =>
  useRequestCommon((data) => authedPostService(config.saveInvoice, data), {
    ...options,
  })

export const usePayOwsi = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.owsiCdPay, query)
    },
    {
      ...options,
    }
  )

export const useGetInvoice = (options) =>
  useRequestCommon(() => authedGetService(config.getInvoice, {}), {
    manual: false,
    ...options,
  })

export const usePayBasicMermaid = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.paybasicMermaid, query)
    },
    {
      ...options,
    }
  )

export const usePayMermaid = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.payMermaidi, query)
    },
    {
      ...options,
    }
  )

export const usePayEfri = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.efriPay, query)
    },
    {
      ...options,
    }
  )

export const usePayFdi = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.payFdi, query)
    },
    {
      ...options,
    }
  )

export const usePayAfdi = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.payAfdi, query)
    },
    {
      ...options,
    }
  )

export const usePayMfdi = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.payMfdi, query)
    },
    {
      ...options,
    }
  )

export const usePaySi = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用方式: 只传 code (number/string)
      const query = typeof params === 'object' && params !== null ? params : { code: params }
      return authedGetService(config.siAppPay, query)
    },
    {
      ...options,
    }
  )

export const useUseInvoice = (options) =>
  useRequestCommon((data) => authedPostService(config.useInvoice, data), {
    ...options,
  })

export const useAnniversaryInstructor = (options) =>
  useRequestCommon(() => authedGetService(config.anniversaryInstructor, {}), {
    manual: false,
    ...options,
  })

export const useEliteInstructor = (options) =>
  useRequestCommon(() => authedGetService(config.eliteInstructor, {}), {
    manual: false,
    ...options,
  })

export const useDownloadEliteAward = (options) => {
  const showErrorModal = useErrorModal()

  return useRequestCommon((data) => authedGetService(config.downEliteAward, data, 'blob'), {
    onSuccess: (blob) => {
      if (blob.type === 'application/json') {
        blob.text().then((text) => {
          const result = JSON.parse(text)
          showErrorModal(result?.error?.clientMsg)
        })
        return
      }
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // a.target = '_blank';
      a.download = 'download.jpg'
      document.body.appendChild(a)
      a.click()
      a.remove()
    },
    ...options,
  })
}

// 教材订单导出：带 token 的 blob 下载（后端已对该端点开启客服鉴权）
export const useDownloadTextbookOrder = (options) => {
  const showErrorModal = useErrorModal()

  return useRequestCommon((data) => authedGetService(config.adminTextbookOrderExcel, data, 'blob'), {
    onSuccess: (blob, params) => {
      // 鉴权失败/业务错误时后端返回 JSON（HTTP 200 + {error:{clientMsg}}），据此弹错
      if (blob.type === 'application/json') {
        blob.text().then((text) => {
          const result = JSON.parse(text)
          showErrorModal(result?.error?.clientMsg)
        })
        return
      }
      const { startDate, endDate } = params?.[0] || {}
      const fileName = startDate && endDate ? `textbooksOrder ${startDate}--${endDate}.xlsx` : 'textbooksOrder.xlsx'
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    },
    ...options,
  })
}

export const useDownloadAnniversaryAward = (options) => {
  const showErrorModal = useErrorModal()

  return useRequestCommon((data) => authedGetService(config.downAnniversaryAward, data, 'blob'), {
    onSuccess: (blob) => {
      if (blob.type === 'application/json') {
        blob.text().then((text) => {
          const result = JSON.parse(text)
          showErrorModal(result?.error?.clientMsg)
        })
        return
      }
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // a.target = '_blank';
      a.download = 'download.jpg'
      document.body.appendChild(a)
      a.click()
      a.remove()
    },
    ...options,
  })
}

export const useInstructorCreate = (options) =>
  useRequestCommon((data) => authedPostService(config.instructorCreate, data), {
    ...options,
  })

export const useSubmitEfri = (options) =>
  useRequestCommon((data) => authedPostService(config.submitEfri, data), {
    ...options,
  })

export const useStripeResult = (options) =>
  useRequestCommon((sessionId) => authedGetService(config.stripeResult, { sessionId }), {
    // manual: false,
    ...options,
  })

export const useSubmitOwsi = (options) =>
  useRequestCommon((data) => authedPostService(config.submitOwsi, data), {
    ...options,
  })

export const useSubmitBasicMermaid = (options) =>
  useRequestCommon((data) => authedPostService(config.submitBasicMermaid, data), {
    ...options,
  })

export const useSubmitMermaid = (options) =>
  useRequestCommon((data) => authedPostService(config.submitMermaidi, data), {
    ...options,
  })

export const useSubmitFdi = (options) =>
  useRequestCommon((data) => authedPostService(config.submitFdi, data), {
    ...options,
  })

export const useSubmitAfdi = (options) =>
  useRequestCommon((data) => authedPostService(config.submitAfdi, data), {
    ...options,
  })

export const useSubmitMfdi = (options) =>
  useRequestCommon((data) => authedPostService(config.submitMfdi, data), {
    ...options,
  })

export const useSubmitSiApp = (options) =>
  useRequestCommon((data) => authedPostService(config.submitSi, data), {
    ...options,
  })

export const useSubmitDm = (options) =>
  useRequestCommon((data) => authedPostService(config.submitDm, data), {
    ...options,
  })

export const useMainlandIp = (options) =>
  useRequestCommon(() => authedGetService(config.getMainlandIp, {}), {
    manual: false,
    ...options,
  })

export const useGetM2PhoneNumber = (options) =>
  useRequestCommon((email) => authedGetService(config.getM2PhoneNumber, { email }), {
    ...options,
  })

export const useTradeVanResult = (options) =>
  useRequestCommon((payToken) => authedGetService(config.tradeVanPayResult, { payToken }), {
    // manual: false,
    ...options,
  })

export const useGetPicOrderList = (current, options) =>
  useRequestCommon(() => authedGetService(config.textbookOrderList, { page: current, size: 15 }), {
    ...options,
  })

export const useGetUnconventionalOrderList = (current, options) =>
  useRequestCommon(() => authedGetService(config.unconventionalOrderList, { page: current, size: 15 }), {
    ...options,
  })

export const useGetInstructorInfo = (options) =>
  useRequestCommon((memberNumber) => authedGetService(config.getInstructorInfo, { memberNumber }), {
    ...options,
  })

export const useGetPicOrderDetail = (id, options) =>
  useRequestCommon(() => authedGetService(config.textbookOrderDetail, { id }), {
    manual: false,
    ...options,
  })
export const useGetUnconventionalOrderDetail = (id, options) =>
  useRequestCommon(() => authedGetService(config.unconventionalOrderDetail, { id }), {
    manual: false,
    ...options,
  })

export const useGetStoreInfo = (options) =>
  useRequestCommon((memberNumber) => authedGetService(config.getStoreInfo, { memberNumber }), {
    ...options,
  })

export const useTextbookOrderPayment = (options) =>
  useRequestCommon(
    (params) => {
      // 兼容旧调用：仅传 id（number/string）或传 { id, channel, payType }
      const query = typeof params === 'object' && params !== null ? params : { id: params }
      // 禁缓存由后端统一处理（/api/** 响应全局下发 no-store，后端 9d1127d81），前端不再单独传
      return authedGetService(config.textbookOrderPayment, query)
    },
    {
      ...options,
    }
  )

export const useUnconventionalOrderPayment = (options) =>
  useRequestCommon((id) => authedGetService(config.unconventionalOrderPayment, { id }), {
    ...options,
  })

export const useChangeOrderStatus = (options) =>
  useRequestCommon((data) => authedPutService(config.adminChangeOrderStatus, data), {
    ...options,
  })

export const useCreateOrder = (options) =>
  useRequestCommon((data) => authedPostService(config.createOrder, data), {
    ...options,
  })

export const useGetRenewCouponList = (options) =>
  useRequestCommon(() => authedGetService(config.renewCouponList, {}), {
    manual: false,
    ...options,
  })

export const useRenewCouponTransfer = (options) =>
  useRequestCommon((data) => authedPostService(config.renewCouponTransfer, data), {
    ...options,
  })

export const useClubVipFuiouPay = (options) =>
  // 禁缓存由后端统一处理（/api/** 响应全局下发 no-store，后端 9d1127d81），前端不再单独传
  useRequestCommon((params) => authedGetService(config.clubVipPaymentUrl, params), {
    ...options,
  })

// CHNA-2618: CLUB VIP 富友 PC 扫码轮询支付状态
export const useClubVipPaymentStatus = (options) =>
  useRequestCommon((params) => authedGetService(config.clubVipPaymentStatus, params), {
    ...options,
  })

// CHNA-2618: 续费富友 PC 扫码轮询支付状态（按订单维度，paid === true 即支付完成；
// 老 clubVip/paymentStatus 不感知富友直连续费单，AUTO_RENEWAL_FUIOU 非法枚举会 400，不可用）
export const useRenewalPaymentStatus = (options) =>
  useRequestCommon((params) => authedGetService(config.renewalPaymentStatus, params), {
    // 轮询场景：ahooks 3.7.11 无 throwOnError 选项（被静默忽略），runAsync 失败恒 reject；
    // 页面用 runAsync 消费 rejection，useFuiouPaymentPolling 的连续失败计数才能生效
    ...options,
  })

// CHNA-2618: 续费关单（扫码弹窗取消/超时兜底）。后端契约（9d1127d81）：
// body 为 { orderId }（字符串，非教材单的 { id }）；响应 { orderId, paid, orderSt }，
// 前端 fire-and-forget 不消费响应——已支付/已关闭时后端幂等返回现状
export const useRenewalCloseOrder = (options) =>
  useRequestCommon((orderId) => authedPostService(config.renewalCloseOrder, { orderId }), {
    ...options,
  })

export const useSubmitBindingMobile = (options) =>
  useRequestCommon((data) => authedPostService(config.submitBindingMobile, data), {
    ...options,
  })

export const useGetVipEcardsReplacementOptions = (options) =>
  useRequestCommon(() => authedGetService(config.vipEcardsReplacementOptions, {}), {
    // manual: false,
    ...options,
  })

export const useBindingEcards = (options) =>
  useRequestCommon((data) => authedPutService(config.ecardImgReBinding, data), {
    ...options,
  })

export const useCancelBindingEcards = (options) =>
  useRequestCommon((data) => authedPutService(config.cancelEcardImgBinding, data), {
    ...options,
  })

export const useSubmitIeOrientation = (options) =>
  useRequestCommon((data) => authedPostService(config.submitIeOrientation, data), {
    ...options,
  })

export const useIeDetail = (options) =>
  useRequestCommon(() => authedGetService(config.owsiDetail, {}), {
    manual: false,
    ...options,
  })

export const useIeStatementList = (ieId, options) =>
  useRequestCommon(() => authedGetService(config.ieStatementList, { ieId }), {
    manual: false,
    ...options,
  })

export const useIeListForExaminer = (options) =>
  useRequestCommon(() => authedGetService(config.ieListForExaminer, {}), {
    manual: false,
    ...options,
  })

export const useIeExamList = (options) =>
  useRequestCommon(() => authedGetService(config.ieList, {}), {
    manual: false,
    ...options,
  })

export const useIeOrientationDetail = (options) =>
  useRequestCommon((code) => authedGetService(config.getIeOrientation, { code }), {
    ...options,
  })

export const useSubmitIeStatement = (options) =>
  useRequestCommon((data) => authedPostService(config.submitIeStatement, data), {
    ...options,
  })

export const useIeStatementDetail = (options) =>
  useRequestCommon((code) => authedGetService(config.getIeStatement, { code }), {
    ...options,
  })

// CHNA-2618: 续费富友 H5PAY/WPOS 下单（取代旧微信 native pay）
export const useMemberRenewFuiouOrder = (options) =>
  useRequestCommon((data) => authedPostService(config.getMemberRenewFuiouOrder, data), {
    ...options,
  })

export const useStoreRenewFuiouOrder = (options) =>
  useRequestCommon((data) => authedPostService(config.getStoreRenewFuiouOrder, data), {
    ...options,
  })

export const useAutoRenew = (options) =>
  useRequestCommon(() => authedGetService(config.autoRenew, {}), {
    manual: false,
    ...options,
  })

export const useAutoRenewSend = (options) =>
  useRequestCommon((data) => authedPostService(config.autoRenewSend, data), {
    ...options,
  })

export const useAutoRenewBind = (options) =>
  useRequestCommon((data) => authedPostService(config.autoRenewBind, data), {
    ...options,
  })

export const useAutoRenewUnBind = (options) =>
  useRequestCommon((data) => authedPostService(config.autoRenewUnBind, data), {
    ...options,
  })

export const useAutoRenewStore = (options) =>
  useRequestCommon(() => authedGetService(config.autoRenewStore, {}), {
    manual: false,
    ...options,
  })

export const useAutoRenewStoreSend = (options) =>
  useRequestCommon((data) => authedPostService(config.autoRenewStoreSend, data), {
    ...options,
  })

export const useAutoRenewStoreBind = (options) =>
  useRequestCommon((data) => authedPostService(config.autoRenewStoreBind, data), {
    ...options,
  })

export const useAutoRenewStoreUnBind = (options) =>
  useRequestCommon((data) => authedPostService(config.autoRenewStoreUnBind, data), {
    ...options,
  })

export const useGetAllCardsImg = (ecardId, options) =>
  useRequestCommon(() => authedGetService(config.allCardsImg, { ecardId }), {
    manual: false,
    ...options,
  })

export const useReSubmitCdSiApp = (options) =>
  useRequestCommon((data) => authedPostService(config.reSubmitCdSiApp, data), {
    ...options,
  })

export const useUploadTrainingRecord = (options) =>
  useRequestCommon((data) => authedPutService(config.uploadTrainingRecord, data), {
    ...options,
  })

export const useUploadCourseReport = (options) =>
  useRequestCommon((data) => authedPutService(config.uploadCourseReport, data), {
    ...options,
  })

export const useCancelAgreements = (options) =>
  useRequestCommon(() => authedPostService(config.cancelAgreement), {
    ...options,
  })

export const useGetVipAllEcardsOptions = (options) =>
  useRequestCommon(() => authedGetService(config.vipAllEcardsOptions, {}), {
    manual: false,
    ...options,
  })

export const useGetValueAdded = (options) =>
  useRequestCommon(() => authedGetService(config.valueAddedServices, {}), {
    manual: false,
    ...options,
  })

export const useCDRenewalDetail = (options) =>
  useRequestCommon(() => authedGetService(config.CDRenewalDetail, {}), {
    ...options,
  })

export const useCDRenewalPayment = (options) =>
  useRequestCommon((data) => authedPostService(config.CDRenewalPayment, data), {
    ...options,
  })

export const useGetExclusiveEcards = (options) =>
  useRequestCommon(() => authedGetService(config.exclusiveEcards, {}), {
    manual: false,
    ...options,
  })

export const useGetAutoRenewPrice = (options) =>
  useRequestCommon(() => authedGetService(config.autoRenePrice, {}), {
    manual: false,
    ...options,
  })

export const useGetStoreAutoRenewPrice = (options) =>
  useRequestCommon(() => authedGetService(config.storeAutoRenewPrice, {}), {
    manual: false,
    ...options,
  })

export const useMailingAddress = (options) =>
  useRequestCommon((data) => authedPostService(config.mailingAddress, data), {
    ...options,
  })

export const useGetVisitStatus = (options) =>
  useRequestCommon(() => authedGetService(config.checkVisitStatus, {}, 'text'), {
    ...options,
  })

export const useSignDivingAgreement = (options) =>
  useRequestCommon(() => authedPostService(config.signDivingAgreement), {
    ...options,
  })

export const useDsdImage = (externalUserId, options) =>
  useRequestCommon((data) => getService(config.dsdImage, { externalUserId }), {
    ...options,
  })

export const useDsdImageByMobile = (options) =>
  useRequestCommon((data) => authedPostService(config.dsdImage, data), {
    ...options,
  })

export const useRescueDrysuitList = (options) =>
  useRequestCommon((data) => authedGetService(config.rescueDrysuitList, data), {
    ...options,
  })

export const useRenewalCurrentYear = (options) =>
  useRequestCommon(() => authedGetService(config.renewalPriceCurrentYear, {}), {
    ...options,
  })

export const useRenewalPriceList = (options) =>
  useRequestCommon((year) => authedGetService(config.renewalPriceList, year ? { renewalYear: year } : {}), {
    ...options,
  })

export const useUpdateRenewalPrice = (options) =>
  useRequestCommon((data) => authedPutService(config.renewalPriceUpdate, data), {
    ...options,
  })

export const useGenerateRenewalPrice = (options) =>
  useRequestCommon((data) => authedPostService(config.renewalPriceGenerate, data), {
    ...options,
  })

export const useRenewalPriceConfig = (options) =>
  useRequestCommon((year) => authedGetService(config.renewalPriceConfig, year ? { renewalYear: year } : {}), {
    ...options,
  })

export const useUpdateRenewalPriceConfig = (options) =>
  useRequestCommon((data) => authedPutService(config.renewalPriceConfigUpdate, data), {
    ...options,
  })

export const useUpdateRenewalPriceSwitches = (options) =>
  useRequestCommon((data) => authedPutService(config.renewalPriceConfigSwitches, data), {
    ...options,
  })

export const useMyPurchasesList = (options) =>
  useRequestCommon((page) => authedGetService(config.myPurchasesList, { page, size: 20 }), {
    ...options,
  })

export const useTextbookPaymentStatus = (options) =>
  useRequestCommon((id) => authedGetService(config.textbookPaymentStatus, { id }), {
    // 轮询场景：ahooks 3.7.11 无 throwOnError 选项（被静默忽略），runAsync 失败恒 reject；
    // 页面用 runAsync 消费 rejection，useFuiouPaymentPolling 的连续失败计数才能生效
    ...options,
  })

export const useTextbookCloseOrder = (options) =>
  useRequestCommon((id) => authedPostService(config.textbookCloseOrder, { id }), {
    ...options,
  })
